/**
 * Super Admin Portal LaTeX Utilities
 *
 * This file enhances the shared UI library with actual KaTeX rendering
 * for the super admin portal.
 */

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import {
  renderLatex as sharedRenderLatex,
  hasLatex as sharedHasLatex,
  renderLatexForTable as sharedRenderLatexForTable
} from '@bayesprep/shared-ui/katex';

// Enhanced renderLatex that uses actual KaTeX rendering
export const renderLatex = (text) => {
  if (!text) return text;

  try {
    // Check if we have LaTeX content
    if (!hasLatex(text)) {
      return React.createElement('span', { className: 'text-content' }, text);
    }

    // Handle block math ($$...$$) first
    const blockMathRegex = /\$\$(.*?)\$\$/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = blockMathRegex.exec(text)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        const beforeText = text.slice(lastIndex, match.index);
        parts.push(renderInlineMath(beforeText, parts.length));
      }

      // Add block math with actual KaTeX
      try {
        parts.push(
          React.createElement('div',
            { key: parts.length, className: 'my-2' },
            React.createElement(BlockMath, { math: match[1] })
          )
        );
      } catch (error) {
        console.warn('Block math rendering failed:', error);
        parts.push(React.createElement('span', { key: parts.length }, match[0]));
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      parts.push(renderInlineMath(remainingText, parts.length));
    }

    return parts.length > 1 ? React.createElement(React.Fragment, {}, ...parts) : parts[0] || text;
  } catch (error) {
    console.warn('LaTeX rendering failed, falling back to shared implementation:', error);
    return sharedRenderLatex(text);
  }
};

// Helper function for inline math
const renderInlineMath = (text, keyOffset = 0) => {
  if (!text) return text;

  try {
    const inlineMathRegex = /\$(.*?)\$/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = inlineMathRegex.exec(text)) !== null) {
      // Add text before the math
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }

      // Add inline math with actual KaTeX
      try {
        parts.push(
          React.createElement(InlineMath, {
            key: keyOffset + parts.length,
            math: match[1]
          })
        );
      } catch (error) {
        console.warn('Inline math rendering failed:', error);
        parts.push(match[0]);
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts.length > 1 ? React.createElement(React.Fragment, {}, ...parts) : parts[0] || text;
  } catch (error) {
    console.warn('Inline math rendering failed:', error);
    return text;
  }
};

// Re-export other functions from shared library
export const hasLatex = (text) => sharedHasLatex(text);
export const renderLatexForTable = (text, maxLength) => sharedRenderLatexForTable(text, maxLength);

// Re-export everything else from the shared library
export * from '@bayesprep/shared-ui/katex';
